// src/pages/PeakHours.jsx
import React, { useEffect, useState, useCallback, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import "./dashboard.scss";

/* Days range options */
const DAYS_OPTIONS = [7, 14];

/* Normalize responses to handle various API payload shapes */
function normalizeArrayResp(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const firstArray = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(firstArray) ? firstArray : [];
}

/* Weekday labels */
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* Palettes for the heatmap chart */
const PALETTES = {
  light: {
    bar: "#2563eb", // Matches enterpriseChartPalette[0]
    peakBar: "#0B2545", // Matches var(--vault-navy)
    heatRanges: [
      { from: 1, to: 1, color: "#EAF2FE" },
      { from: 2, to: 3, color: "#C7DBFB" },
      { from: 4, to: 7, color: "#9ABEF6" },
      { from: 8, to: 15, color: "#6299F1" },
      { from: 16, to: 99999, color: "#0B2545" },
    ],
  },
  dark: {
    bar: "#2563eb", // Matches enterpriseChartPalette[0]
    peakBar: "#E6EAF2", // Matches var(--vault-navy) in dark mode
    heatRanges: [
      { from: 1, to: 1, color: "rgba(31, 111, 235, 0.15)" },
      { from: 2, to: 3, color: "rgba(31, 111, 235, 0.4)" },
      { from: 4, to: 7, color: "#6299F1" },
      { from: 8, to: 15, color: "#1F6FEB" },
      { from: 16, to: 99999, color: "#0B2545" },
    ],
  },
};

export default function PeakHours() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const [daysRange, setDaysRange] = useState(7);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("hourly"); // 'hourly' | 'heatmap'

  // Chart data states
  const [hourCounts, setHourCounts] = useState(new Array(24).fill(0));
  const [heatmapSeries, setHeatmapSeries] = useState([]); 
  const [activeHourCats, setActiveHourCats] = useState([]); 
  const [fetchedAt, setFetchedAt] = useState(null);

  // Unified chart height
  const chartHeight = 240;

  const fetchData = useCallback(
    async (useRange = daysRange) => {
      setLoading(true);
      try {
        const resp = await request(`logs?page=1&limit=10000&eventType=login`, { method: "GET" }).catch(async () => {
          return await request(`logs?page=1&limit=10000`, { method: "GET" });
        });

        const logs = normalizeArrayResp(resp);
        const now = new Date();
        const start = new Date(now.getTime() - useRange * 24 * 60 * 60 * 1000);

        const hoursAgg = new Array(24).fill(0);
        const weekHour = Array.from({ length: 7 }, () => new Array(24).fill(0));

        for (const lg of logs) {
          if (!lg) continue;
          
          let ts =
            lg.timestamp ||
            lg.createdAt ||
            (lg.eventData && lg.eventData.data && lg.eventData.data.timestamp) ||
            lg.time ||
            (lg.eventData && lg.eventData.login && lg.eventData.login.createdAt) ||
            null;
          
          if (!ts && typeof lg === "string") ts = lg;
          if (!ts) continue;
          
          const d = new Date(ts);
          if (isNaN(d.getTime())) continue;
          if (d < start || d > now) continue;

          const hr = d.getHours();
          const wd = d.getDay(); // 0..6
          hoursAgg[hr] = (hoursAgg[hr] || 0) + 1;
          weekHour[wd][hr] = (weekHour[wd][hr] || 0) + 1;
        }

        setHourCounts(hoursAgg);

        // Determine which hours have any activity
        const actIdx = [];
        for (let i = 0; i < 24; i++) {
          const totalForHour = weekHour.reduce((acc, row) => acc + (row[i] || 0), 0);
          if (totalForHour > 0) actIdx.push(i);
        }
        const activeCats = actIdx.map((i) => `${String(i).padStart(2, "0")}:00`);
        setActiveHourCats(activeCats);

        // Build heatmap series using only active hours so columns align
        const heatSeries = WEEKDAY_LABELS.map((label, idx) => {
          const row = weekHour[idx] || new Array(24).fill(0);
          const data = actIdx.map((hour) => ({ x: `${String(hour).padStart(2, "0")}:00`, y: row[hour] || 0 }));
          return { name: label, data };
        });

        setHeatmapSeries(heatSeries);
        setFetchedAt(new Date());
      } catch (err) {
        console.error("PeakHours fetch error:", err);
        setHourCounts(new Array(24).fill(0));
        setHeatmapSeries([]);
        setActiveHourCats([]);
      } finally {
        setLoading(false);
      }
    },
    [daysRange]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, daysRange]);

  // Derive filtered bar categories & data
  const activeIndices = activeHourCats.map((h) => parseInt(h.slice(0, 2), 10));
  const filteredBarCats = activeHourCats.length ? activeHourCats : [];
  const filteredBarData = activeHourCats.length ? activeIndices.map((i) => hourCounts[i] || 0) : [];

  // Compute peak hour label and counts
  let peakHourLabel = "—";
  let peakCount = 0;
  if (hourCounts.some((c) => c > 0)) {
    let max = -1;
    let idx = 0;
    hourCounts.forEach((c, i) => {
      if (c > max) {
        max = c;
        idx = i;
      }
    });
    peakHourLabel = `${String(idx).padStart(2, "0")}:00`;
    peakCount = max;
  }

  const pal = isDark ? PALETTES.dark : PALETTES.light;
  const chartTextColor = isDark ? "#9AA7BD" : "#6B7A90"; // Maps to var(--slate)
  const chartBorderColor = isDark ? "rgba(255, 255, 255, 0.12)" : "#E4E9F0"; // Maps to var(--hairline)

  // Hourly Bar Chart Options
  const barOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: true },
      foreColor: chartTextColor,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%",
        borderRadius: 4,
        distributed: true,
      },
    },
    legend: { show: false },
    fill: {
      type: "solid",
      opacity: 1,
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: filteredBarCats,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: "11px" } },
    },
    yaxis: { title: { text: "Logins" } },
    tooltip: { 
      theme: isDark ? "dark" : "light",
      y: { formatter: (v) => `${v} logins` } 
    },
    colors: filteredBarData.map((v) => (v === peakCount && v > 0 ? pal.peakBar : pal.bar)),
    grid: { 
      borderColor: chartBorderColor,
      strokeDashArray: 4
    },
  };
  const barSeries = [{ name: "Logins", data: filteredBarData }];

  // Heatmap Options
  const heatOptions = {
    chart: {
      height: chartHeight,
      type: "heatmap",
      toolbar: { show: false },
      foreColor: chartTextColor,
    },
    dataLabels: { enabled: false },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.8,
        radius: 4,
        colorScale: {
          ranges: isDark ? PALETTES.dark.heatRanges : PALETTES.light.heatRanges,
        },
      },
    },
    tooltip: { 
      theme: isDark ? "dark" : "light",
      y: { formatter: (v) => `${v} logins` } 
    },
    legend: { show: true, position: "top" },
    grid: { borderColor: chartBorderColor },
  };

  return (
    <div className="widget-card" style={{ width: "100%" }}>
      <div 
        className="global-card" 
        style={{ 
          height: "100%", 
          display: "flex", 
          flexDirection: "column", 
          padding: 0,
          position: "relative" 
        }}
      >
        <div style={{ padding: "0.9rem 0.9rem 0 0.9rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h5 className="global-section-title" style={{ margin: 0 }}>
              Peak Login Hours
            </h5>
            <div className="global-caption text-secondary" style={{ marginTop: 4 }}>
              Last <strong>{daysRange} days</strong>. Peak:{" "}
              <strong className="text-interactive">{peakHourLabel}</strong>{" "}
              <small>({peakCount} logins)</small>
            </div>
          </div>

          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            {/* View Mode Toggle */}
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                className={`global-button ${viewMode === "hourly" ? "global-button-primary" : "global-button-outlined"}`}
                onClick={() => setViewMode("hourly")}
                style={{ padding: "4px 12px", minHeight: "28px", fontSize: "13px" }}
              >
                Hourly
              </button>
              <button
                className={`global-button ${viewMode === "heatmap" ? "global-button-primary" : "global-button-outlined"}`}
                onClick={() => setViewMode("heatmap")}
                style={{ padding: "4px 12px", minHeight: "28px", fontSize: "13px" }}
              >
                Heatmap
              </button>
            </div>

            {/* Days Range Toggle */}
            <div style={{ display: "flex", gap: "6px" }}>
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  className={`global-button ${daysRange === d ? "global-button-primary" : "global-button-outlined"}`}
                  onClick={() => setDaysRange(d)}
                  style={{ padding: "4px 12px", minHeight: "28px", fontSize: "13px" }}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: "0 0.9rem 0.6rem 0.9rem", flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
          <div style={{ minHeight: chartHeight, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", flex: "1" }}>
            
            {/* Loading Overlay */}
            {loading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark ? "rgba(11, 18, 33, 0.7)" : "rgba(255, 255, 255, 0.7)",
                  zIndex: 6,
                  borderRadius: 8
                }}
              >
                <div className="text-secondary" style={{ fontWeight: 600 }}>Calculating…</div>
              </div>
            )}

            {/* Chart Rendering */}
            <div style={{ width: "100%", height: "100%" }}>
              {viewMode === "hourly" ? (
                filteredBarCats.length === 0 ? (
                  <div style={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="text-secondary">No login events in selected range</div>
                  </div>
                ) : (
                  <ReactApexChart options={barOptions} series={barSeries} type="bar" height={chartHeight} />
                )
              ) : heatmapSeries.length === 0 || activeHourCats.length === 0 ? (
                <div style={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div className="text-secondary">No heatmap data</div>
                </div>
              ) : (
                <ReactApexChart options={heatOptions} series={heatmapSeries} type="heatmap" height={chartHeight} />
              )}
            </div>
          </div>

          {/* Footer Metadata */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 8 }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span className="global-chip bg-mist text-secondary" style={{ padding: "4px 10px", fontSize: "13px", fontWeight: "normal" }}>
                Hours shown: {activeHourCats.length}
              </span>
              <span className="global-chip bg-mist text-secondary" style={{ padding: "4px 10px", fontSize: "13px", fontWeight: "normal" }}>
                Total logins: {hourCounts.reduce((a, b) => a + b, 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}